import React, { useState } from 'react';
import { User, Mail, Phone, GraduationCap, MapPin, X, Check, Loader2 } from 'lucide-react';
import { AdmissionData } from '../types';
import { useCourses } from '../hooks/useData';

interface CourseSelectionFormProps {
  data: AdmissionData;
  onChange: (field: keyof AdmissionData, value: any) => void;
  onSubmit: (result?: any) => void;
  onCancel: () => void;
}

export const CourseSelectionForm: React.FC<CourseSelectionFormProps> = ({ data, onChange, onSubmit, onCancel }) => {
  const { courses, loading } = useCourses();
  const [localLoading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!data.student_name || !data.student_phone || !data.selected_course || !data.city) return;

    try {
      setLoading(true);
      onSubmit({ type: 'course-selection', data, result: { success: true } });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-white to-indigo-50/50">
      <div className="flex-shrink-0 px-6 py-5 flex justify-between items-center bg-white border-b border-gray-100 shadow-sm z-10">
        <div>
          <h3 className="font-sans font-bold text-2xl text-gray-900 tracking-tight">Admission Inquiry</h3>
          <p className="text-xs text-indigo-600 font-bold tracking-widest uppercase mt-1.5 flex items-center gap-1">
            <GraduationCap size={12} /> Request a callback from Aziz Edu Admissions
          </p>
        </div>
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all duration-300 transform hover:rotate-90">
          <X size={24} />
        </button>
      </div>

      <div className="flex-grow overflow-y-auto px-6 py-6 custom-scrollbar space-y-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
          <h4 className="font-sans font-bold text-gray-900 border-b border-gray-50 pb-2 mb-4">Personal Information</h4>

          <div className="space-y-4">
            <FloatingInput
              label="Full Name"
              value={data.student_name}
              onChange={(e: any) => onChange('student_name', e.target.value)}
              required
              icon={User}
            />

            <div className="grid grid-cols-2 gap-x-6">
              <FloatingInput
                label="Phone Number"
                type="tel"
                value={data.student_phone}
                onChange={(e: any) => onChange('student_phone', e.target.value)}
                required
                icon={Phone}
              />
              <FloatingInput
                label="Email (Optional)"
                type="email"
                value={data.student_email}
                onChange={(e: any) => onChange('student_email', e.target.value)}
                icon={Mail}
              />
            </div>

            <FloatingInput
              label="City"
              value={data.city || ''}
              onChange={(e: any) => onChange('city', e.target.value)}
              required
              icon={MapPin}
            />
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 space-y-4">
          <h4 className="font-sans font-bold text-gray-900 border-b border-gray-50 pb-2 mb-4">Program Selection</h4>

          <div className="space-y-4">
            <FloatingSelect
              label="Select Program"
              value={data.selected_course}
              onChange={(e: any) => onChange('selected_course', e.target.value)}
              options={[
                { value: "", label: "" },
                { value: "BS Computer Science (CS)", label: "BS Computer Science (CS)" },
                { value: "BS Software Engineering (SE)", label: "BS Software Engineering (SE)" },
                { value: "BS Artificial Intelligence (AI)", label: "BS Artificial Intelligence (AI)" },
                { value: "BS Cybersecurity", label: "BS Cybersecurity" },
                { value: "BS Data Science", label: "BS Data Science" },
                { value: "BS Cloud Computing", label: "BS Cloud Computing" },
                { value: "BS Information Technology (IT)", label: "BS Information Technology (IT)" },
                { value: "BS Robotics & Automation", label: "BS Robotics & Automation" }
              ]}
              icon={GraduationCap}
              required
            />
          </div>
        </div>
      </div>

      <div className="px-6 pb-2 text-center text-[10px] text-gray-400 font-medium italic">
        * This is an inquiry form. Our admission representative will contact you shortly for final processing.
      </div>
      <div className="p-6 bg-white border-t border-gray-100 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <button
          onClick={handleSubmit}
          disabled={localLoading || !data.student_name || !data.student_phone || !data.selected_course || !data.city}
          className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl hover:shadow-indigo-500/30 transition-all duration-300 transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 group"
        >
          {localLoading ? <Loader2 className="animate-spin" /> : <>Submit Inquiry <Check size={20} className="group-hover:translate-x-1 transition-transform" /></>}
        </button>
      </div>
    </div>
  );
};

// Reusable components for the form
const FloatingInput = ({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = " ",
  className = "",
  icon: Icon
}: any) => (
  <div className={`relative z-0 w-full mb-6 group ${className}`}>
    {Icon && <Icon size={18} className="absolute left-0 top-3.5 text-gray-400 group-focus-within:text-indigo-600 transition-colors duration-300" />}
    <input
      type={type}
      value={value}
      onChange={onChange}
      className={`block py-3.5 ${Icon ? 'pl-8' : 'pl-0'} pr-0 w-full text-base text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-indigo-500 peer transition-all duration-300 font-medium placeholder-transparent`}
      placeholder={placeholder}
      required={required}
    />
    <label className={`peer-focus:font-medium absolute text-sm text-gray-600 duration-300 transform -translate-y-6 scale-75 top-3.5 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-indigo-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 ${Icon ? 'peer-placeholder-shown:translate-x-8' : ''} peer-focus:scale-75 peer-focus:-translate-y-6 peer-focus:translate-x-0`}>
      {label}
    </label>
  </div>
);

const FloatingSelect = ({
  label,
  value,
  onChange,
  options,
  required = false,
  icon: Icon
}: any) => (
  <div className="relative z-0 w-full mb-6 group">
    {Icon && <Icon size={18} className="absolute left-0 top-3.5 text-gray-400 group-focus-within:text-indigo-600 transition-colors duration-300" />}
    <select
      value={value}
      onChange={onChange}
      className={`block py-3.5 ${Icon ? 'pl-8' : 'pl-0'} pr-0 w-full text-base text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-indigo-500 peer transition-all duration-300 font-medium bg-none cursor-pointer`}
      required={required}
    >
      <option value="" disabled hidden> </option>
      {options.map((opt: any) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <label className={`peer-focus:font-medium absolute text-sm text-gray-600 duration-300 transform -translate-y-6 scale-75 top-3.5 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-indigo-600 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 ${Icon ? 'peer-placeholder-shown:translate-x-8' : ''} peer-focus:scale-75 peer-focus:-translate-y-6 peer-focus:translate-x-0`}>
      {label}
    </label>
  </div>
);
